import {
  SkyButton,
  SkyDataGrid,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { CSSProperties } from "react";
import { StatusCell } from "@/components/runs/StatusCell";
import { BREAKABLE_NAME_CLASS, breakableName } from "@/lib/breakableName";
import { LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import type { MonitorCheckRow } from "@shared/types/monitor";
import {
  NO_FRESHNESS_LIMIT_HINT,
  formatAmount,
  freshnessText,
  hasNoFreshnessLimit,
  valueInRange,
  type WatchedTable,
} from "@/lib/monitor/format";
import { MonitorStatusBadge } from "./MonitorStatusBadge";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The Tables tab (spec section 11): one row per watched table, its worst monitor's status, when it
 * last loaded, its age against its limit, and its rows against the usual range. The table name is
 * a button that opens the history drawer. Times are in the reader's display zone (round 3); the
 * page names it once.
 */
/**
 * Fixes wave 4b (post-release check 4, 03b-monitor-tables.png, 1440 px): the fixed 960 px floor was
 * too low for seven headers, so the load time touched "Age / limit" and "Rows last load / usual
 * range" ran under the check time. Border-box widths measured in Chromium (16 px semibold headers,
 * the larger of the app's stack and Inter; a sortable header adds its 22 px icon): the times hold
 * their headers and a 14 px display-zone time (round 3, 2026-09-30: 196 px with the cell's
 * padding) on one line, Status 113 px and Age / limit 125 px. The one header too long for any
 * share, "Rows last load / usual range" (287 px), wraps at a space to two lines ("Rows last load /"
 * needs 175 px). The floor, 1120 px, fits the pane at 1440 px; a narrower screen scrolls sideways.
 *
 * Round 6 W15 (walk 2026-10-01: "dbo.cust...", "feedback...", "Azure SQL test so..."): the
 * Connection and Table cells wrap instead of cutting the name, and the Table column shows the
 * warehouse table as skyData's load records name it (`tableName`, the load's recorded `targetTable`) with
 * the connector's own name for it under it, muted, when the two differ. A table whose freshness
 * has no limit because no schedule covers its connection says so beside its Unknown.
 */
/**
 * Walk 2026-10-02, D11 (at 1280 px the grid scrolled sideways, 1120 px in 971; table names took 4
 * or 5 lines at 1440 px; "200 rows (usual 180 t..." was cut): the two times are two lines at their
 * comma ("May 28, 2026," 100 px at 14 px) under their sortable headers (Last load 137 px, Checked
 * 130), the rows and range wrap instead of truncating under a header on three lines ("load /
 * usual" 105 px), and a name breaks between words or after an underscore, never mid-word. The
 * floor is 960 px, inside the pane at 1280 px.
 *
 * Walk of 9a9d586 (2026-10-02, D-f: at 1440 px a table name took four lines while Connection had
 * room to spare): the three share columns grew alike past the floor. Connection (its sortable
 * header's 156 px) and Rows (its header on three lines, 137 px) are pixel columns now and Table is
 * the one share column, so at 1440 px it is 291 px and "MOUSEFLOW_SANDBOX_JOURNEY_REPORT_DAILY"
 * reads on two lines (each half 194 px at 14 px semibold). The floor is 968 px, still inside the
 * pane at 1280 px, where a name may take more lines.
 *
 * Round 7 T-36 (owner: at 1280 px a table name took four to six lines): every column but Table is
 * at its header's need, so the names are clamped instead: Table and Connection at two lines, the
 * connector's own name under the table at one, each with an ellipsis and the whole name in its
 * `title` (and whole in the DOM, so a screen reader reads it all).
 */
const STATUS_WIDTH = 120;
const LAST_LOAD_WIDTH = 144;
const AGE_WIDTH = 128;
const CHECKED_WIDTH = 136;
const CONNECTION_WIDTH = 160;
const VOLUME_WIDTH = 152;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  displayName: 128,
};

/** "(usual 900 to 1,100)", "(expected at least 13,500)", or nothing while there is no range. */
function rangeWords(check: MonitorCheckRow): string {
  if (check.expectedLow !== null && check.expectedHigh !== null) {
    return ` (usual ${Math.round(check.expectedLow).toLocaleString()} to ${Math.round(
      check.expectedHigh,
    ).toLocaleString()})`;
  }
  if (check.expectedLow !== null) {
    return ` (expected at least ${Math.round(check.expectedLow).toLocaleString()})`;
  }
  return "";
}

/**
 * #1221 (finding 68): the rows the judged load brought and the rows in total, each named, the
 * usual range on the count the check compares with it ("1,000 rows in the last load", "12,400
 * rows in total (usual 12,000 to 13,000)"). A full-refresh table's value is its total, so it is
 * never shown as the last load's rows. Without a recorded load the checked count stands alone,
 * claiming neither.
 */
function rowsLines(check: MonitorCheckRow, lastLoadAt: string | null): string[] {
  const counts = check.loadCounts;
  if (!counts || counts.basis === null) return [valueInRange(check)];
  const range = rangeWords(check);
  // Round 1 review of #1272: the counts are the load the check judged. When another check of the
  // table has seen a newer load (the Last load column), that load is not "the last load".
  const older = olderThan(check.signalAt, lastLoadAt);
  const judged = older ? "the load checked" : "the last load";
  // Round 2: the total is then the total after that older load, not the table's total now.
  const total = older ? "in total after the load checked" : "in total";
  return [
    counts.lastLoadRows === null
      ? `Rows in ${judged} not recorded`
      : `${formatAmount(counts.lastLoadRows, "rows")} in ${judged}${counts.basis === "loaded" ? range : ""}`,
    counts.totalRecords === null
      ? `Rows ${total} not recorded`
      : `${formatAmount(counts.totalRecords, "rows")} ${total}${counts.basis === "total" ? range : ""}`,
  ];
}

/** True when instant `a` is before instant `b`; false when either is missing. */
function olderThan(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  return Date.parse(a) < Date.parse(b);
}

/** Round 6 W15: a name breaks onto another line rather than being cut; D11: never mid-word. */
const WRAP = BREAKABLE_NAME_CLASS;

export function MonitorTablesGrid({
  rows,
  onOpen,
}: {
  rows: WatchedTable[];
  onOpen: (row: WatchedTable) => void;
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<WatchedTable>[] = [
    {
      key: "connectionName",
      header: "Connection",
      width: `${CONNECTION_WIDTH}px`,
      sortable: true,
      renderCell: (_value, row) => (
        <SkyText
          size={300}
          block
          lineClamp={2}
          className={WRAP}
          title={row.connectionName ?? "Deleted connection"}
        >
          {row.connectionName ? breakableName(row.connectionName) : "Deleted connection"}
        </SkyText>
      ),
    },
    {
      key: "displayName",
      header: "Table",
      // D-f: the one share column, so every pixel past the floor goes to the name.
      width: "100%",
      sortable: true,
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <SkyButton
            appearance="transparent"
            size="small"
            onClick={() => onOpen(row)}
            testId={`monitor-table-open-${row.entityName}`}
            className="h-auto min-h-7 max-w-full justify-start py-1 text-left"
          >
            <SkyText
              size={300}
              weight="semibold"
              color="accent"
              block
              lineClamp={2}
              className={WRAP}
              title={row.displayName}
            >
              {breakableName(row.displayName)}
            </SkyText>
          </SkyButton>
          {row.tableName !== null && row.tableName !== row.entityName ? (
            <SkyText
              size={200}
              color="muted"
              block
              lineClamp={1}
              className={`${WRAP} px-2.5`}
              title={row.entityName}
              testId={`monitor-table-source-name-${row.entityName}`}
            >
              {breakableName(row.entityName)}
            </SkyText>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: `${STATUS_WIDTH}px`,
      sortable: true,
      // `StatusCell`: a badge straight in a cell draws as a bar across the column.
      renderCell: (_value, row) => {
        const badge = (
          <MonitorStatusBadge
            status={row.status}
            testId={`monitor-table-status-${row.entityName}`}
          />
        );
        if (row.status !== "unknown" || !hasNoFreshnessLimit(row)) {
          return <StatusCell badge={badge} />;
        }
        // Round 6 W15: why Unknown, on hover and keyboard focus, and for a screen reader in the
        // cell itself (`client/AGENTS.md`: a hint that is not the cell's own text).
        return (
          <StatusCell
            badge={
              <SkyTooltip content={NO_FRESHNESS_LIMIT_HINT}>
                <span tabIndex={0} aria-describedby={undefined} className="inline-flex">
                  {badge}
                  <span className="sr-only">{NO_FRESHNESS_LIMIT_HINT}</span>
                </span>
              </SkyTooltip>
            }
          />
        );
      },
    },
    {
      key: "lastLoadAt",
      header: "Last load",
      width: `${LAST_LOAD_WIDTH}px`,
      sortable: true,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" title={instantHoverTitle(row.lastLoadAt, zone)}>
          {row.lastLoadAt ? formatInstant(row.lastLoadAt, zone, "minutes") : "Not loaded yet"}
        </SkyText>
      ),
    },
    {
      key: "freshness",
      header: "Age / limit",
      width: `${AGE_WIDTH}px`,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric">
          {row.freshness ? freshnessText(row.freshness) : "Not watched"}
        </SkyText>
      ),
    },
    {
      key: "volume",
      // #1221: the header names neither count; the cell says which is which.
      header: "Rows",
      width: `${VOLUME_WIDTH}px`,
      // Walk 2026-10-02, D11: the range wraps (it truncated to "200 rows (usual 180 t...").
      renderCell: (_value, row) => {
        const lines = row.volume ? rowsLines(row.volume, row.lastLoadAt) : ["Not watched"];
        const title = lines.length > 1 ? `${lines.join(". ")}.` : lines[0];
        return (
          // Capture of 2026-10-16: at 1024 px each count wraps, so a gap keeps the two apart.
          <div
            title={title}
            className="flex flex-col gap-1"
            data-testid={`monitor-table-rows-${row.entityName}`}
          >
            {lines.map((line) => (
              <SkyText key={line} size={300} font="numeric" block title={title}>
                {line}
              </SkyText>
            ))}
          </div>
        );
      },
    },
    {
      key: "lastEvalAt",
      header: "Checked",
      width: `${CHECKED_WIDTH}px`,
      sortable: true,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" title={instantHoverTitle(row.lastEvalAt, zone)}>
          {row.lastEvalAt ? formatInstant(row.lastEvalAt, zone, "minutes") : "Not yet"}
        </SkyText>
      ),
    },
  ];
  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    <div style={{ "--monitor-tables-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<WatchedTable>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.key}
        {...LIST_GRID_PAGINATION}
        className="[&_table]:min-w-(--monitor-tables-floor) [&_th]:whitespace-normal"
        ariaLabel="Watched tables"
        emptyMessage="No table matches these filters."
        testId="monitor-tables-grid"
      />
    </div>
  );
}
